"use client";

/**
 * AdminDrawer — 화면 가장자리에서 나오는 패널 (우선순위 9)
 *
 * Sheet 기반. 상세보기, 보조 폼, 모바일 필터 등에 사용한다. 제어 컴포넌트.
 * side 로 등장 방향을, size 로 너비를 조절한다. (좌/우일 때 적용)
 *
 * @example
 * <AdminDrawer
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   side="right"
 *   title="회원 상세"
 *   footer={<Button onClick={() => setOpen(false)}>닫기</Button>}
 * >
 *   ...상세 내용...
 * </AdminDrawer>
 */
import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

export type AdminDrawerSide = "left" | "right" | "top" | "bottom";
export type AdminDrawerSize = "sm" | "md" | "lg" | "xl";

export interface AdminDrawerProps {
  open: boolean;
  onClose: () => void;
  /** 등장 방향 (기본 right) */
  side?: AdminDrawerSide;
  /** 드로어 제목 */
  title?: ReactNode;
  /** 제목 아래 보조 설명 */
  description?: ReactNode;
  /** 본문 (길면 스크롤된다) */
  children?: ReactNode;
  /** 하단 고정 버튼 영역 */
  footer?: ReactNode;
  /** 너비 (좌/우 방향일 때 적용, 기본 md) */
  size?: AdminDrawerSize;
  className?: string;
}

const WIDTH: Record<AdminDrawerSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
};

export function AdminDrawer({
  open,
  onClose,
  side = "right",
  title,
  description,
  children,
  footer,
  size = "md",
  className,
}: AdminDrawerProps) {
  const isHorizontal = side === "left" || side === "right";
  // className 으로 너비(w-*/max-w-*)를 직접 주면 size·기본 max-w 캡을 풀어 그 값이 적용되게 한다.
  const hasCustomWidth = !!className && /(?:^|\s)(?:max-)?w-/.test(className);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side={side}
        className={cn(
          isHorizontal &&
            (hasCustomWidth ? "sm:max-w-none" : WIDTH[size]),
          className
        )}
        // 헤더를 primary 로 칠했을 때 X 버튼도 그 위에서 보이도록 색을 맞춘다.
        closeClassName={
          title || description ? "text-primary-foreground" : undefined
        }
      >
        {/* Radix Dialog 접근성을 위해 제목은 항상 둔다 (없으면 숨김) */}
        {title || description ? (
          <SheetHeader className="bg-primary">
            <SheetTitle className="text-primary-foreground font-bold">{title}</SheetTitle>
            {description && <SheetDescription className="text-ring">{description}</SheetDescription>}
          </SheetHeader>
        ) : (
          <SheetTitle className="sr-only">패널</SheetTitle>
        )}

        <div className="flex-1 overflow-y-auto px-4">{children}</div>

        {footer && <SheetFooter>{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  );
}
